import { useState, useMemo, useEffect, useCallback, FormEvent } from 'react'
import * as Yup from 'yup'
import { useFormik } from 'formik'
import debounce from 'lodash/debounce'
import { useTranslation } from 'react-i18next'

import { FoundationNameWithFlag } from '@percent/admin-dashboard/common/components'
import { useFoundations, useQueryList } from '@percent/admin-dashboard/common/hooks'
import {
  Alert,
  Checkbox,
  Flex,
  Icon,
  AsyncSelect,
  Select,
  FormField,
  Modal,
  ActionModal,
  AcknowledgeModal,
  TextInput,
  DateRange
} from '@percent/lemonade'
import { useServices } from '@percent/admin-dashboard/containers/service/ServiceContext'
import { CreateDonationRequestsBatchModalProps } from './CreateDonationRequestsBatchModal.types'
import styles from './CreateDonationRequestsBatchModal.module.scss'
import { dayJS, getUTCDayStart } from '@percent/admin-dashboard/common/library/utility/date'
import {
  ALL_DONATION_CATEGORIES,
  ALL_DONATION_KINDS,
  DonationRequestDonationCategory,
  DonationRequestDonationKind
} from '@percent/admin-dashboard/api/actions/donationRequests/donationRequests.types'

enum SubmitResult {
  Success = 'success',
  Error = 'error',
  NoDonationsError = 'noDonationsError'
}

const THROTTLING_TIME = 500

export function CreateDonationRequestsBatchModal({ open, onClose, onSuccess }: CreateDonationRequestsBatchModalProps) {
  const { t } = useTranslation()
  const { foundations } = useFoundations()
  const { donationRequestsService, adminService } = useServices()
  const [isDatePickerOpened, setIsDatePickerOpened] = useState(false)
  const [submitResult, setSubmitResult] = useState<SubmitResult | null>(null)

  const defaultDateRange = {
    startDate: dayJS().subtract(1, 'day').toDate(),
    endDate: dayJS().subtract(1, 'day').toDate()
  }

  const {
    values,
    setFieldValue,
    setValues,
    errors,
    handleSubmit,
    isValid,
    isSubmitting,
    dirty,
    resetForm,
    touched,
    setFieldTouched
  } = useFormik<{
    startDate: undefined | Date
    endDate: undefined | Date
    partnerId: string
    preferredFoundationId: string
    importBatchId: string
    donationKinds: DonationRequestDonationKind[]
    donationCategories: DonationRequestDonationCategory[]
  }>({
    initialValues: {
      startDate: undefined,
      endDate: undefined,
      partnerId: '',
      preferredFoundationId: '',
      importBatchId: '',
      donationKinds: [...ALL_DONATION_KINDS],
      donationCategories: [...ALL_DONATION_CATEGORIES]
    },

    validationSchema: () =>
      Yup.object().shape({
        startDate: Yup.date().required(t('typography.required')),
        endDate: Yup.date().required(t('typography.required')),
        partnerId: Yup.string().required(t('typography.required')),
        preferredFoundationId: Yup.string(),
        importBatchId: Yup.string(),
        donationKinds: Yup.array()
          .of(Yup.mixed().oneOf([...ALL_DONATION_KINDS]))
          .min(1, t('dialog.createDonationRequestsBatch.form.donationTypes.error')),
        donationCategories: Yup.array()
          .of(Yup.mixed().oneOf([...ALL_DONATION_CATEGORIES]))
          .min(1, t('dialog.createDonationRequestsBatch.form.donationCategories.error'))
      }),
    onSubmit: async ({
      partnerId,
      startDate,
      endDate,
      preferredFoundationId,
      importBatchId,
      donationKinds,
      donationCategories
    }) => {
      try {
        await donationRequestsService.createDonationRequestsBatch({
          partnerId,
          startDate: getUTCDayStart(startDate!).toISOString(),
          endDate: dayJS(endDate!).endOf('day').toISOString(),
          preferredFoundationId: preferredFoundationId || undefined,
          importBatchId: importBatchId || undefined,
          donationKinds: donationKinds || undefined,
          // selecting every category means no filtering, so the param is omitted
          category: ALL_DONATION_CATEGORIES.every(category => donationCategories.includes(category))
            ? undefined
            : donationCategories
        })

        onSuccess()
        setSubmitResult(SubmitResult.Success)
      } catch (e) {
        if (e?.response?.data?.error?.code === 'donation_request/no_donations_found') {
          setSubmitResult(SubmitResult.NoDonationsError)
        } else {
          setSubmitResult(SubmitResult.Error)
        }
      }
    }
  })

  const handleDonationKindChange = useCallback(
    (event: FormEvent<HTMLInputElement>) => {
      const { checked, value } = event.currentTarget
      const donationKind = ALL_DONATION_KINDS.find(kind => kind === value)

      if (!donationKind) return

      setFieldValue(
        'donationKinds',
        checked ? [...values.donationKinds, donationKind] : values.donationKinds.filter(kind => kind !== donationKind)
      )
    },
    [setFieldValue, values.donationKinds]
  )

  function setDonationCategory(newCategory: DonationRequestDonationCategory, active: boolean) {
    if (active) {
      setFieldValue('donationCategories', [...values.donationCategories, newCategory])
    } else {
      setFieldValue(
        'donationCategories',
        values.donationCategories?.filter(donationCategory => donationCategory !== newCategory)
      )
    }
  }

  const defaultOption = useMemo(
    () => ({
      label: t('dialog.createDonationRequestsBatch.form.searchPartners'),
      value: ''
    }),
    [t]
  )

  const [partnersOptions, setPartnersOptions] = useState([defaultOption])
  const [partnerOptionsFilter, setPartnerOptionsFilter] = useState('')

  const [{ dataOrNull, error, isLoading }, { query }] = useQueryList(adminService.queries.getPartnerList, {
    pageSize: 25
  })

  const foundationsOptions = useMemo(() => {
    return Object.values(foundations || {}).map(foundation => ({
      title: <FoundationNameWithFlag countryCode={foundation.countryCode} name={foundation.name} />,
      value: foundation.id
    }))
  }, [foundations])

  const selectedOption = useMemo(() => {
    return partnersOptions.find(option => option.value === values.partnerId) || partnersOptions[0]
  }, [partnersOptions, values.partnerId])

  useEffect(() => {
    if (!isLoading && !error && dataOrNull && partnerOptionsFilter) {
      const newOptions = dataOrNull.map(({ name, id }) => ({
        label: name,
        value: id
      }))

      const uniqueOptions = [defaultOption, selectedOption, ...newOptions].reduce(
        (accumulator: { label: string; value: string }[], current) => {
          if (!accumulator.find(item => item.value === current.value)) {
            accumulator.push(current)
          }

          return accumulator
        },
        []
      )

      setPartnersOptions(uniqueOptions)
    }
  }, [dataOrNull, defaultOption, error, isLoading, partnerOptionsFilter, selectedOption])

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedQuery = useCallback(
    debounce<any>((filter: string) => {
      query({ query: filter })
    }, THROTTLING_TIME),
    []
  )
  useEffect(() => {
    if (partnerOptionsFilter) {
      debouncedQuery(partnerOptionsFilter)
    }
  }, [partnerOptionsFilter, debouncedQuery])

  const handleClose = () => {
    resetForm()
    setSubmitResult(null)
    onClose()
  }

  const handleDateRangeClick = () => {
    setFieldTouched('startDate', true)
    setFieldTouched('endDate', true)
    setIsDatePickerOpened(true)
  }

  const successModal = submitResult === SubmitResult.Success && (
    <AcknowledgeModal
      result="positive"
      title={t('dialog.createDonationRequestsBatch.success')}
      buttonText={t('button.close')}
      handleClose={handleClose}
      viewTestId="create-donation-requests-batch-success-modal"
      buttonTestId="close-create-donation-requests-batch-success-modal"
    />
  )

  const errorModal = (submitResult === SubmitResult.Error || submitResult === SubmitResult.NoDonationsError) && (
    <AcknowledgeModal
      result="negative"
      title={
        submitResult === SubmitResult.NoDonationsError
          ? t('toast.createDonationRequestsBatchNoDonationsError')
          : t('dialog.createDonationRequestsBatch.error')
      }
      buttonText={t('button.done')}
      handleClose={handleClose}
      viewTestId="create-donation-requests-batch-error-modal"
      buttonTestId="close-create-donation-requests-batch-error-modal"
    />
  )

  const dateRangeQueryParams = useMemo(() => {
    if (values.startDate && values.endDate) {
      return {
        startDate: dayJS(values.startDate).format('YYYY-MM-DDTHH:mm:ssZ'),
        endDate: dayJS(values.endDate).format('YYYY-MM-DDTHH:mm:ssZ')
      }
    }

    if (isDatePickerOpened) {
      return {
        startDate: dayJS(defaultDateRange.startDate).format('YYYY-MM-DDTHH:mm:ssZ'),
        endDate: dayJS(defaultDateRange.endDate).format('YYYY-MM-DDTHH:mm:ssZ')
      }
    }

    return {
      startDate: undefined,
      endDate: undefined
    }
  }, [defaultDateRange.endDate, defaultDateRange.startDate, isDatePickerOpened, values.endDate, values.startDate])

  return (
    <Modal open={open} onClose={handleClose}>
      {successModal || errorModal || (
        <ActionModal
          title={t('dialog.createDonationRequestsBatch.header')}
          primaryButtonText={t('button.create')}
          primaryBtnTestId="create-donation-requests-batch-submit"
          secondaryButtonText={t('button.cancel')}
          secondaryBtnTestId="create-donation-requests-batch-cancel"
          handleClose={handleClose}
          handleSubmit={handleSubmit}
          disabled={!(isValid && dirty)}
          loading={isSubmitting}
        >
          <Flex direction="column" gap={16}>
            <Alert variant="info" title={t('dialog.createDonationRequestsBatch.info')} />

            {partnersOptions && (
              <FormField label={t('dropdown.partner')} necessity="required">
                <AsyncSelect
                  name="partner"
                  placeholder={t('dialog.createDonationRequestsBatch.form.searchPartners')}
                  loading={isLoading}
                  options={partnersOptions.filter(option => option.value !== '')}
                  setQuery={q => setPartnerOptionsFilter(q.trim())}
                  query={partnerOptionsFilter}
                  onChange={e => {
                    setFieldTouched('partnerId')
                    setFieldValue('partnerId', e?.value || '')
                  }}
                  defaultValue={selectedOption?.value ? selectedOption : undefined}
                  loadingText="Loading..."
                  noResultsFoundText={t('errorMessage.noResult')}
                  data-testid="partnerSearchableSelect"
                />
              </FormField>
            )}

            <FormField
              label={t('dialog.createDonationRequestsBatch.form.donationTypes')}
              necessity="required"
              status={errors.donationKinds ? 'danger' : 'default'}
              statusMessage={errors.donationKinds as string | undefined}
            >
              <Flex direction="row" gap={16} align="center">
                {ALL_DONATION_KINDS.map(kind => (
                  <Checkbox
                    key={kind}
                    name={`${kind}DonationKind`}
                    value={kind}
                    label={t(`dialog.createDonationRequestsBatch.form.${kind}`)}
                    active={values.donationKinds?.includes(kind)}
                    onChange={handleDonationKindChange}
                    variant="default"
                    dataTestId={`${kind}DonationKindCheckbox`}
                  />
                ))}
              </Flex>
            </FormField>

            <FormField
              label={t('dialog.createDonationRequestsBatch.form.donationCategories')}
              necessity="required"
              status={errors.donationCategories ? 'danger' : 'default'}
              statusMessage={errors.donationCategories as string | undefined}
            >
              <Flex direction="column" gap={16} align="flex-start" justify="flex-start" wrap="wrap">
                <Flex direction="row" gap={16} align="center">
                  <Checkbox
                    name="grantDonationCategory"
                    label={t('dialog.createDonationRequestsBatch.form.donationCategories.grant')}
                    active={values.donationCategories?.includes('grant')}
                    onChange={event => {
                      setDonationCategory('grant', (event.target as HTMLInputElement).checked)
                    }}
                    variant="default"
                    dataTestId="grantDonationCategoryCheckbox"
                  />
                  <Checkbox
                    name="employeeImpactDonationCategory"
                    label={t('dialog.createDonationRequestsBatch.form.donationCategories.employeeImpact')}
                    active={values.donationCategories?.includes('employee_impact')}
                    onChange={event => {
                      setDonationCategory('employee_impact', (event.target as HTMLInputElement).checked)
                    }}
                    variant="default"
                    dataTestId="employeeImpactDonationCategoryCheckbox"
                  />
                  <Checkbox
                    name="consumerGivingDonationCategory"
                    label={t('dialog.createDonationRequestsBatch.form.donationCategories.consumerGiving')}
                    active={values.donationCategories?.includes('consumer_giving')}
                    onChange={event => {
                      setDonationCategory('consumer_giving', (event.target as HTMLInputElement).checked)
                    }}
                    variant="default"
                    dataTestId="consumerGivingDonationCategoryCheckbox"
                  />
                </Flex>
                <Flex direction="row" gap={16} align="center">
                  <Checkbox
                    name="corporateDonationDonationCategory"
                    label={t('dialog.createDonationRequestsBatch.form.donationCategories.corporateDonation')}
                    active={values.donationCategories?.includes('corporate_donation')}
                    onChange={event => {
                      setDonationCategory('corporate_donation', (event.target as HTMLInputElement).checked)
                    }}
                    variant="default"
                    dataTestId="corporateDonationDonationCategoryCheckbox"
                  />
                  <Checkbox
                    name="unclassifiedDonationCategory"
                    label={t('dialog.createDonationRequestsBatch.form.donationCategories.unclassified')}
                    active={values.donationCategories?.includes('unclassified')}
                    onChange={event => {
                      setDonationCategory('unclassified', (event.target as HTMLInputElement).checked)
                    }}
                    variant="default"
                    dataTestId="unclassifiedDonationCategoryCheckbox"
                  />
                </Flex>
              </Flex>
            </FormField>

            {foundationsOptions && (
              <FormField label={t('dialog.createDonationRequestsBatch.form.foundation')}>
                <Select
                  placeholder={t('dialog.createDonationRequestsBatch.form.selectFoundation')}
                  options={foundationsOptions.map(option => ({
                    label: option.title as unknown as string,
                    value: option.value
                  }))}
                  defaultValue={
                    values.preferredFoundationId
                      ? {
                          label:
                            (foundationsOptions.find(option => option.value === values.preferredFoundationId)
                              ?.title as unknown as string) || '',
                          value: values.preferredFoundationId
                        }
                      : undefined
                  }
                  onChange={({ value }) => {
                    setFieldValue('preferredFoundationId', value || '')
                  }}
                  data-testid="foundationSelect"
                />
              </FormField>
            )}

            <FormField
              label={t('dialog.createDonationRequestsBatch.form.date')}
              necessity="required"
              status={
                (touched.startDate || touched.endDate) && (errors.startDate || errors.endDate) ? 'danger' : 'default'
              }
              statusMessage={
                touched.startDate || touched.endDate
                  ? ((errors.startDate || errors.endDate) as string | undefined)
                  : undefined
              }
            >
              <div className={styles.datePickerContainer}>
                <DateRange
                  open={isDatePickerOpened}
                  setOpen={setIsDatePickerOpened}
                  queryParams={dateRangeQueryParams}
                  setQueryParams={params => {
                    if (typeof params === 'object' && params.startDate && params.endDate) {
                      setValues({
                        startDate: dayJS(params.startDate).toDate(),
                        endDate: dayJS(params.endDate).toDate(),
                        partnerId: values.partnerId,
                        preferredFoundationId: values.preferredFoundationId,
                        importBatchId: values.importBatchId,
                        donationKinds: values.donationKinds,
                        donationCategories: values.donationCategories
                      })
                    }
                  }}
                  maxDate={defaultDateRange.endDate}
                  applyButtonText={t('button.apply')}
                  withinPortal={false}
                >
                  <div
                    className={styles.datePickerWrapper}
                    onClick={handleDateRangeClick}
                    onKeyDown={e => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault()
                        handleDateRangeClick()
                      }
                    }}
                    role="button"
                    tabIndex={0}
                  >
                    <TextInput
                      className={styles.datePickerInput}
                      name="date-range"
                      placeholder={t('dialog.createDonationRequestsBatch.form.selectDateRange')}
                      value={
                        values.startDate && values.endDate
                          ? `${dayJS(values.startDate).format('DD MMM YYYY')} - ${dayJS(values.endDate).format(
                              'DD MMM YYYY'
                            )}`
                          : ''
                      }
                      readOnly
                      tabIndex={-1}
                    />
                    <Icon name="dropdown-arrow-down" size={8} color="gray600" />
                  </div>
                </DateRange>
              </div>
            </FormField>

            <FormField label={t('dialog.createDonationRequestsBatch.form.importBatchId')}>
              <TextInput
                name="import-batch-id"
                placeholder={t('dialog.createDonationRequestsBatch.form.importBatchId.placeholder')}
                value={values.importBatchId}
                onChange={e => {
                  setFieldTouched('importBatchId')
                  setFieldValue('importBatchId', e.target.value)
                }}
              />
            </FormField>
          </Flex>
        </ActionModal>
      )}
    </Modal>
  )
}
